import {HeaderTitle, Icon} from '@/components/atoms'
import {CustomDataTable, UploadHistoryTableModal, FileUploadWithModal} from '@/components/organisms'
import useUploadHistory from '@/hooks/useUploadHistory'
import {useCountryProductTabs} from '@/hooks/useCountryProductTabs'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {Header} from '@/utils/constants/header'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useCallback, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {MAIN_COLUMNS, UI_CONSTANTS} from './InventoryListConstants'
import {useInventory} from './useInventory'
import {useInventoryActions} from './useInventoryActions'
import {downloadInventoryCSVFiles} from '@/helpers/shadcn/utils'
import {Constants} from '@/utils/constants/commonConstants'
import {TabsComponent} from '@/components/molecules'
import {toSafeRouteParam} from '@/utils/security/pathSafety'

/**
 * Generates a filename with current timestamp for inventory template
 * @returns {string} Formatted filename with timestamp
 */
const getInventoryTemplateFilename = () => {
  const date = new Date()
  const timestamp = date.toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0]
  return `Inventory Template_${timestamp}`
}

const InventoryList = () => {
  const inventoryTableRef = useRef(null)
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  const [rowSelected, setRowSelected] = useState([])
  const navigate = useNavigate()
  const [isSelectAll, setSelectAll] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)
  const [formData, setFormData] = useState({
    targetMediaFile: [],
  })
  const countryCode = useSelector(selectCountryCode)

  const {
    activeProductTabIndex: activeInventoryTabIndex,
    handleProductTabChange: handleInventoryTabChange,
    productTabList: inventoryTabList,
    productTabFilters: inventoryTabFilters,
  } = useCountryProductTabs(countryCode, inventoryTableRef)

  const {
    inventoryData,
    inventoryColConfigData,
    setSortingObj,
    setSearchArry,
    onPageIndexChange,
    onDownloadBtnClick,
    inventoryResult,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
    csvData,
    handleUploadSuccess,
  } = useInventory({isSelectAll, filters: inventoryTabFilters})
  const {onUploadHistoryPageIndexChange, uploadHistoryData, downloadUploadError} = useUploadHistory(
    UPLOAD_HISTORY_TYPE?.INVENTORY,
    openUploadHistory
  )
  
  // Use custom hook for business logic separation
  const {handleResetAll} = useInventoryActions({
    inventoryData,
    rowSelected,
    onDownloadBtnClick,
    setSortingObj,
    setSearchArry,
    setRowSelected,
    createSearchArray,
  })

  // Transform columns to CustomDataTable format
  const transformedColumns = useMemo(
    () =>
      inventoryColConfigData.map(col => ({
        name_of_db_column: col.columnKey || col.name_of_db_column,
        label_of_column: col.header || col.label_of_column,
        filterVariant: col.filterVariant || 'text',
        // Add clickable configuration for productId
        ...((col.columnKey || col.name_of_db_column) === 'productId' && {
          isClickable: true,
          onCellClick: value => {
            const safeValue = toSafeRouteParam(value)

            if (safeValue) {
              navigate(ROUTES.PRODUCT_REVIEW.replace(':id', safeValue))
            }
          },
        }),
      })),
    [inventoryColConfigData, navigate]
  )

  // Handle sorting for CustomDataTable
  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  // Handle row selection for CustomDataTable
  const handleRowSelect = selectedRows => {
    setRowSelected(selectedRows)
  }

  // Handle reset all for tab changes
  const handleResetAllForTabChange = useCallback(() => {
    setSortingObj({})
    createSearchArray([])
    setRowSelected([])
    setSelectAll(false)
    onPageIndexChange(1)
    resetAllBtnClicked()
    inventoryTableRef?.current?.resetAll?.()
  }, [setSortingObj, createSearchArray, onPageIndexChange, resetAllBtnClicked])

  // Handle inventory tab change
  const handleInventoryTabChangeWithReset = useCallback(
    index => {
      if (index === activeInventoryTabIndex) {
        return
      }
      handleResetAllForTabChange()
      handleInventoryTabChange(index)
    },
    [activeInventoryTabIndex, handleResetAllForTabChange, handleInventoryTabChange]
  )

  // Handle page index change for CustomDataTable
  const handlePageIndexChange = pageIndex => {
    onPageIndexChange(pageIndex)
  }

  // Define the actions dynamically
  const tableActions = useMemo(
    () => [
      {
        id: 'template-upload',
        label: 'Add Template',
        onClick: () => setOpenUpload(true),
      },
      {
        id: 'view-upload-history',
        label: 'View Uploaded History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        divider: true, // This creates a separator
      },
      {
        id: 'download',
        label: 'Download',
        onClick: ({selectedRows = []} = {}) => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelected
          const dataToDownload = selectedRows.length > 0 ? selectedRows : rowSelected
          if (dataToDownload.length > 0) {
            onDownloadBtnClick(dataToDownload)
          }
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [onDownloadBtnClick, rowSelected]
  )

  const columns = MAIN_COLUMNS(downloadUploadError)

  // Determine if any sub row has locationId
  const allRows = (uploadHistoryData?.data?.rows || uploadHistoryData?.data || []);
  const hasLocationId = allRows.some(row => !!row.locationId);

  // Build subColumns dynamically
  const dynamicSubColumns = useMemo(() => [
    { columnKey: 'productId', header: 'Product ID' },
    ...(!hasLocationId ? [{ columnKey: 'warehouseId', header: 'Warehouse ID' }] : []),
    { columnKey: 'errorMsg', header: 'STATUS (REMARK)' },
  ], [hasLocationId]);

  // Build inventory table component
  const inventoryTable = (
    <div className="relative" style={{backgroundColor: UI_CONSTANTS.DARK_BACKGROUND}}>
      <HeaderTitle
        headingLevel={Header.SectionTitle}
        title={UI_CONSTANTS.PAGE_TITLE}
        className="inventoryDataTableHeading pl-4 pr-4"
      />
      <span className="inventorySettingsIcon absolute h-8 w-8 rounded p-2">
        <Icon icon="settings" isDark={true} size="small" className="h-4 w-4" data-testid="settings-icon" />
      </span>

      <div className="px-4">
        <CustomDataTable
          ref={inventoryTableRef}
          columns={transformedColumns}
          records={inventoryData?.data || []}
          isLoading={inventoryResult.isLoading}
          pageSize={DEFAULT_TABLE_PAGE_SIZE}
          totalRecords={inventoryData?.totalRecords || 0}
          setPageIndex={handlePageIndexChange}
          handleSorting={handleSorting}
          setSearchArry={setSearchArry}
          onResetAll={() => {
            resetAllBtnClicked()
            handleResetAll()
          }}
          onRowSelect={handleRowSelect}
          actions={tableActions}
          showActions={true}
          isSelectAll={isSelectAll}
          setSelectAll={setSelectAll}
          defaultSortConfig={{key: 'productId', direction: 'desc'}}
        />
      </div>
    </div>
  )

  return (
    <div className="inventoryContainer flex flex-col">
      {inventoryTabList?.length ? (
        <TabsComponent
          tabList={inventoryTabList.map(item => ({
            ...item,
            tabComponent: inventoryTable,
          }))}
          defaultTabIndex={activeInventoryTabIndex}
          onTabChange={handleInventoryTabChangeWithReset}
        />
      ) : (
        inventoryTable
      )}

      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData.data}
        totalRecords={uploadHistoryData.totalRecords}
        mainColumns={columns}
        subColumns={dynamicSubColumns}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        setPageIndex={onUploadHistoryPageIndexChange}
        data-testid="upload-history-modal"
      />

      <FileUploadWithModal
        openUpload={openUpload}
        setOpenUpload={setOpenUpload}
        setFormData={setFormData}
        formData={formData}
        title="Upload Inventory Template"
        showTertiaryButton={true}
        uploadFor={'inventory'}
        isCsvValidationRequired={true}
        isTemplateUpload={true}
        required={false}
        label={
          <span>
            {Constants.uploadInventory}{' '}
            <span
              tabIndex={0}
              role="button"
              onClick={() => downloadInventoryCSVFiles(csvData, getInventoryTemplateFilename())}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  downloadInventoryCSVFiles(csvData, getInventoryTemplateFilename())
                }
              }}
              style={{textDecoration: 'underline', color: '#007bff', cursor: 'pointer'}}
            >
              here
            </span>
            .
          </span>
        }
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  )
}

export default InventoryList
